'use client'

/**
 * Potwierdzenia w stylu systemu — zamiast natywnego `confirm()` przeglądarki.
 * Natywne okno pokazuje adres strony („Komunikat ze strony heizos.app"), ma wygląd
 * systemu operacyjnego i nie da się go opisać ani odróżnić operacji groźnej od zwykłej.
 *
 * Użycie:
 *   const confirm = useConfirm()
 *   if (!(await confirm({ title: 'Usunąć zlecenie?', danger: true }))) return
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'

export interface ConfirmOptions {
  title: string
  /** Doprecyzowanie: co dokładnie się stanie i czego nie da się cofnąć. */
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  /** true = operacja niszcząca → czerwony przycisk i ikona ostrzeżenia. */
  danger?: boolean
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const Ctx = createContext<ConfirmFn | null>(null)

export function useConfirm(): ConfirmFn {
  const ctx = useContext(Ctx)
  // Fallback na natywne okno, gdyby ktoś użył hooka poza providerem — lepsze to
  // niż wywalenie się aplikacji przy próbie usunięcia czegoś.
  return ctx ?? (async (o) => window.confirm(o.title))
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((v: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o)
    return new Promise<boolean>((resolve) => { resolver.current = resolve })
  }, [])

  const close = (value: boolean) => {
    resolver.current?.(value)
    resolver.current = null
    setOpts(null)
  }

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {opts && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
          onClick={() => close(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-installer-card border dark:border-installer-border border-gray-2 shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5">
              <span
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  opts.danger ? 'bg-danger/12 text-danger' : 'bg-[#0066CC]/12 text-[#0066CC]'
                }`}
              >
                <AlertTriangle className="w-5 h-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-[15px] font-semibold dark:text-white text-dark">{opts.title}</h3>
                {opts.description && (
                  <p className="text-xs text-gray-1 mt-1 leading-relaxed">{opts.description}</p>
                )}
              </div>
              <button
                onClick={() => close(false)}
                className="text-gray-1 hover:dark:text-white hover:text-dark flex-shrink-0"
                aria-label="Schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 dark:bg-installer-surface bg-gray-3 border-t dark:border-installer-border border-gray-2">
              <button
                onClick={() => close(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium dark:bg-installer-card bg-white border dark:border-installer-border border-gray-2 dark:text-white text-dark hover:border-gray-1/40 transition-colors"
              >
                {opts.cancelLabel || 'Abbrechen'}
              </button>
              <button
                onClick={() => close(true)}
                autoFocus
                className={`px-4 py-2 rounded-xl text-sm font-semibold text-white transition-colors ${
                  opts.danger ? 'bg-danger hover:bg-danger/85' : 'bg-brand hover:bg-brand-strong'
                }`}
              >
                {opts.confirmLabel || (opts.danger ? 'Löschen' : 'Bestätigen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  )
}
